import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Award, Briefcase, Target, Zap, Rocket, Building2, UserCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AIPlan({ user }) {
    const [userPlan, setUserPlan] = useState(null);
    const [companyPlan, setCompanyPlan] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const [userRes, companyRes] = await Promise.all([
                    axios.get(`${BACKEND_URL}/api/ai-plan/user`, { withCredentials: true }),
                    user.company_id ? axios.get(`${BACKEND_URL}/api/ai-plan/company`, { withCredentials: true }) : Promise.resolve({ data: null })
                ]);

                setUserPlan(userRes.data);
                setCompanyPlan(companyRes.data);
            } catch (error) {
                console.error('Error fetching AI plans:', error);
                toast.error("Erro ao carregar o seu plano. Tente novamente mais tarde.");
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, [user]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation user={user} />
                <div className="flex justify-center items-center h-[60vh]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Navigation user={user} />

            <main className="container mx-auto px-4 md:px-8 py-16">
                <div className="mb-12">
                    <div className="flex items-center gap-3 mb-4">
                        <Target className="h-8 w-8 text-primary" strokeWidth={1.5} />
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Dashboard AI First
                        </h1>
                    </div>
                    <p className="text-lg text-muted-foreground max-w-2xl">
                        O hub central de inteligência da sua jornada, consolidando suas respostas nos frameworks em diretrizes acionáveis.
                    </p>
                </div>

                <Tabs defaultValue="personal" className="w-full">
                    <TabsList className="mb-8 p-1 bg-secondary/50 inline-flex">
                        <TabsTrigger value="personal" className="data-[state=active]:bg-background px-6 py-2 rounded-md transition-all flex items-center gap-2">
                            <UserCircle className="h-4 w-4" /> Plano Individual
                        </TabsTrigger>
                        {user.company_id && (
                            <TabsTrigger value="company" className="data-[state=active]:bg-background px-6 py-2 rounded-md transition-all flex items-center gap-2">
                                <Building2 className="h-4 w-4" /> Visão da Empresa
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="personal" className="space-y-8 animate-in fade-in-50 duration-500">
                        {/* Header Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card className="p-6 bg-primary/5 border border-primary/20 flex items-center gap-4 hover:bg-primary/10 transition-colors">
                                <div className="bg-primary/20 p-3 rounded-full text-primary">
                                    <Award className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-muted-foreground uppercase tracking-wide">Progresso</p>
                                    <p className="text-2xl font-bold text-foreground">
                                        {userPlan?.responses ? Object.keys(userPlan.responses).length : 0} Frameworks
                                    </p>
                                </div>
                            </Card>

                            <Card className="p-6 bg-card border border-border flex flex-col justify-center lg:col-span-2">
                                <div className="flex items-start gap-3">
                                    <Zap className="h-5 w-5 text-yellow-500 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-bold mb-1">Resumo Direto</h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            {userPlan?.summary || "Preencha os frameworks na aba correspondente para gerar seu plano consolidado."}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* Detailed sections based on responses */}
                        <h3 className="text-2xl font-bold mt-12 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Detalhes Consolidados</h3>

                        {userPlan?.responses && Object.keys(userPlan.responses).length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Object.entries(userPlan.responses).map(([fw_id, data]) => (
                                    <Card key={fw_id} className="p-6 border border-border bg-card hover:shadow-lg transition-all">
                                        <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                                            <Rocket className="h-5 w-5 text-primary" />
                                            Framework {fw_id.replace('fw_', '').toUpperCase()}
                                        </h4>
                                        <div className="space-y-3">
                                            {Object.entries(data).map(([key, val]) => {
                                                if (Array.isArray(val)) {
                                                    return (
                                                        <div key={key} className="text-sm border-t border-border pt-2 mt-2">
                                                            <span className="font-medium text-muted-foreground block mb-2">{key.toUpperCase()}:</span>
                                                            <ul className="pl-4 list-disc space-y-1">
                                                                {val.slice(0, 3).map((v, i) => (
                                                                    <li key={i}>{v.titulo || JSON.stringify(v).substring(0, 20) + '...'}</li>
                                                                ))}
                                                                {val.length > 3 && <li className="text-muted-foreground">+{val.length - 3} itens</li>}
                                                            </ul>
                                                        </div>
                                                    )
                                                }
                                                return (
                                                    <div key={key} className="flex justify-between items-center text-sm border-b border-border/50 pb-2">
                                                        <span className="text-muted-foreground capitalize">{key}</span>
                                                        <span className="font-bold">{val}</span>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-12 border border-dashed border-border rounded-xl text-muted-foreground bg-secondary/30">
                                Você ainda não preencheu nenhum framework.
                                <br /> <br />
                                <a href="/frameworks" className="text-primary font-bold hover:underline">Ir para Frameworks</a>
                            </div>
                        )}
                    </TabsContent>

                    {user.company_id && companyPlan && (
                        <TabsContent value="company" className="space-y-8 animate-in fade-in-50 duration-500">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <Card className="p-6 bg-secondary border border-border">
                                    <Briefcase className="h-8 w-8 text-muted-foreground mb-4" />
                                    <h3 className="text-xl font-bold mb-2">{companyPlan.company_name || 'Sua Empresa'}</h3>
                                    <p className="text-muted-foreground text-sm mb-6">
                                        Visão unificada das análises de IA conduzidas pelos membros da empresa.
                                    </p>

                                    <div className="space-y-4">
                                        <div>
                                            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Membros Participantes</span>
                                            <span className="text-3xl font-black">{companyPlan.total_participants}</span>
                                        </div>
                                    </div>
                                </Card>

                                <Card className="p-6 bg-card border border-border lg:col-span-2 flex flex-col justify-center">
                                    <h3 className="font-bold text-lg mb-3">Diagnóstico Global</h3>
                                    <div className="p-4 bg-muted/50 rounded-lg text-muted-foreground leading-relaxed border border-border/50">
                                        {companyPlan.company_summary}
                                    </div>
                                </Card>
                            </div>



                        </TabsContent>
                    )}

                </Tabs>
            </main>
        </div>
    );
}
